import { Colors, DxfWriter, LWPolylineFlags, type LWPolylineVertex, point2d, Units } from "@tarikjabiri/dxf";
import { inject, injectable } from "inversify";
import { type IService, Logger, type XYZ } from "@designboticteam/core";
import { SnapshotService, toPlanar, type ZoneType } from "@designboticteam/kolega-3d";
import { MapLibreInstanceService, ProjectionService } from "@designboticteam/maplibre";

const BUILDING_LINE_LAYER = "BuildingLine";

const LAYER_COLORS: Record<ZoneType | typeof BUILDING_LINE_LAYER, Colors> = {
	CheckZone: Colors.Green,
	BuildingZone: Colors.Blue,
	ExcludedZone: Colors.Red,
	BuildingLine: Colors.Magenta,
};

/**
 * Zones as closed and building lines as open LWPOLYLINEs on one layer per type, in EPSG:2180
 * metres (DXF X = easting, Y = northing, so north is up in CAD).
 */
@injectable()
class DxfExportService implements IService {
	public readonly serviceName = "DxfExportService";
	private readonly logger = new Logger(`📐 ${this.serviceName}`, { backgroundColor: "#ffffff", color: "#000000" });

	constructor(
		@inject(MapLibreInstanceService) private readonly mapLibreInstanceService: MapLibreInstanceService,
		@inject(SnapshotService) private readonly snapshotService: SnapshotService,
		@inject(ProjectionService) private readonly projectionService: ProjectionService,
	) {}

	public initialize(): void {
		this.logger.log("initialized");
	}

	public dispose(): void {
		this.logger.log("disposed");
	}

	/** The DXF text, or null when there is neither a zone nor a building line to draw. */
	public buildDxf(): string | null {
		const { zoneTool, buildingLineTool } = this.snapshotService.getSnapshot();

		if (zoneTool.zones.length === 0 && buildingLineTool.buildingLines.length === 0) {
			return null;
		}

		const writer = new DxfWriter();
		writer.setUnits(Units.Meters);

		for (const [layer, color] of Object.entries(LAYER_COLORS)) {
			writer.addLayer(layer, color, "Continuous");
		}

		for (const zone of zoneTool.zones) {
			writer.setCurrentLayerName(zone.zoneType);
			writer.addLWPolyline(this.toVertices(zone.points), { flags: LWPolylineFlags.Closed });
		}

		for (const buildingLine of buildingLineTool.buildingLines) {
			writer.setCurrentLayerName(BUILDING_LINE_LAYER);
			writer.addLWPolyline(this.toVertices(buildingLine.points));
		}

		return writer.stringify();
	}

	private toVertices(points: Array<XYZ>): Array<LWPolylineVertex> {
		return points.map((point) => {
			const [x, z] = toPlanar(point);
			const [easting, northing] = this.projectionService.wgs84ToPolandCs92(this.mapLibreInstanceService.sceneToWgs84({ x, z }));

			return { point: point2d(easting, northing) };
		});
	}
}

export { DxfExportService };
